import { DestroyRef, Injectable, Signal, computed, effect, inject, signal, untracked } from '@angular/core';
import { Router } from '@angular/router';
import { Subject, debounceTime, take } from 'rxjs';
import {
  BRIDGE_WINDOW,
  BridgeEndpoint,
  BridgePeer,
  EditorToHostMessage,
  EmbeddedAuthPayload,
  HostToEditorMessage,
  MAX_OPEN_PROJECTS,
  OpenProjectKind,
  OpenProjectRecord,
  ProjectOpenMode,
  USER_SETTINGS_SERVICE,
  normalizeOpenMode,
  openProjectKindOf,
  parseEditorToHost,
  toOrigin,
} from '@infinitform/platform-core';
import { ConfirmDialogService, ThemeService, ToastService } from '@infinitform/shared-ui';
import { environment } from '../config/cabinet-environment';
import { AuthService } from '../services/security/auth.service';
import { OpenableProject, OpenProjectsApi } from './open-projects.tokens';
import {
  AUTH_REQUEST_RENEW_COOLDOWN_MS,
  HIBERNATE_ACK_TIMEOUT_MS,
  OPEN_PROJECTS_WARM_LIMIT,
  OpenProjectState,
  PERSIST_DEBOUNCE_MS,
  PLACEHOLDER_PROJECT_NAME,
  PoolMember,
  READY_TIMEOUT_MS,
  editorProjectUrl,
  isProjectId,
  mergeRestored,
  pickHibernations,
  sanitizeOpenProjects,
} from './open-projects.policy';

/** An open project with what the cabinet knows about its editor right now. */
export interface OpenProjectView extends OpenProjectRecord {
  state: OpenProjectState;
  /** True while the frame boots or its editor is still loading the project. */
  loading: boolean;
  dirty: boolean;
  snapshotUrl: string | null;
  frameTitle: string;
}

/** An iframe the host should render; a new key means a fresh iframe. */
export interface MountedFrame {
  id: string;
  key: number;
}

/** The part of an iframe the service talks to. */
export interface EditorFrame {
  readonly contentWindow: BridgePeer | null;
}

interface EntryRuntime {
  state: OpenProjectState;
  /** What the editor last said about loading its project. */
  editorLoading: boolean;
  dirty: boolean;
  snapshotUrl: string | null;
  title: string | null;
}

const IDLE: EntryRuntime = { state: 'cold', editorLoading: false, dirty: false, snapshotUrl: null, title: null };

function toView(record: OpenProjectRecord, runtime: EntryRuntime): OpenProjectView {
  return {
    ...record,
    state: runtime.state,
    loading: runtime.state === 'booting' || (runtime.editorLoading && (runtime.state === 'live' || runtime.state === 'warm')),
    dirty: runtime.dirty,
    snapshotUrl: runtime.snapshotUrl,
    frameTitle: `Project: ${runtime.title ?? record.name}`,
  };
}

/**
 * Projects open inside the cloud cabinet: the persisted list, one editor
 * iframe per mounted project, the visible editor plus the paused ones behind
 * it, and the bridge to each editor. By default every open project stays
 * loaded; `OPEN_PROJECTS_WARM_LIMIT` only matters if configured lower.
 */
@Injectable()
export class OpenProjectsService implements OpenProjectsApi {
  private readonly settings = inject(USER_SETTINGS_SERVICE);
  private readonly auth = inject(AuthService);
  private readonly toast = inject(ToastService);
  private readonly confirmDialog = inject(ConfirmDialogService);
  private readonly router = inject(Router);
  private readonly theme = inject(ThemeService);
  private readonly win = inject(BRIDGE_WINDOW);
  private readonly warmLimit = inject(OPEN_PROJECTS_WARM_LIMIT);
  private readonly editorOrigin = toOrigin(environment.EDITOR_ADDRESS);

  readonly available = this.editorOrigin !== null;

  private readonly records = signal<readonly OpenProjectRecord[]>([]);
  private readonly runtime = signal<ReadonlyMap<string, EntryRuntime>>(new Map());
  private readonly mountedFrames = signal<readonly MountedFrame[]>([]);
  private readonly activeIdSignal = signal<string | null>(null);
  private readonly mode = signal<ProjectOpenMode>('ask');

  readonly openMode: Signal<ProjectOpenMode> = this.mode.asReadonly();
  readonly activeId: Signal<string | null> = this.activeIdSignal.asReadonly();
  readonly mounted: Signal<readonly MountedFrame[]> = this.mountedFrames.asReadonly();
  readonly views = computed<readonly OpenProjectView[]>(() => {
    const runtime = this.runtime();
    return this.records().map((record) => toView(record, runtime.get(record.id) ?? IDLE));
  });
  readonly activeView = computed<OpenProjectView | null>(() => {
    const id = this.activeIdSignal();
    return id ? this.views().find((view) => view.id === id) ?? null : null;
  });
  readonly hasDirty = computed(() => this.views().some((view) => view.dirty));

  private readonly endpoints = new Map<string, BridgeEndpoint<EditorToHostMessage, HostToEditorMessage>>();
  private readonly readyIds = new Set<string>();
  private readonly timers = new Map<string, ReturnType<typeof setTimeout>>();
  private readonly persistRequests = new Subject<void>();
  private restoreStatus: 'idle' | 'loading' | 'done' = 'idle';
  private generation = 0;
  private persistPending = false;
  private mountCounter = 0;
  private lastGrant: EmbeddedAuthPayload | null = null;
  private lastRenewedAt: number | null = null;
  private renewing = false;

  constructor() {
    const persistSubscription = this.persistRequests
      .pipe(debounceTime(PERSIST_DEBOUNCE_MS))
      .subscribe(() => this.flushNow());
    this.win.addEventListener('pagehide', this.onPageHide);

    effect(() => {
      const grant = this.auth.embeddedAuth();
      untracked(() => this.onGrant(grant));
    });

    effect(() => {
      const mode = this.theme.currentTheme();
      untracked(() => this.broadcast({ type: 'theme', payload: { mode } }));
    });

    inject(DestroyRef).onDestroy(() => {
      persistSubscription.unsubscribe();
      this.win.removeEventListener('pagehide', this.onPageHide);
      this.teardown();
    });
  }

  /** Loads the saved list and open mode; mounts nothing. */
  restore(): void {
    if (!this.available || this.restoreStatus !== 'idle') return;
    this.restoreStatus = 'loading';
    const generation = this.generation;
    this.settings
      .get()
      .pipe(take(1))
      .subscribe({
        next: (stored) => {
          if (generation !== this.generation) return;
          const local = this.records();
          const merged = mergeRestored(sanitizeOpenProjects(stored.openProjects), local);
          const dropped = local.filter((record) => !merged.some((entry) => entry.id === record.id)).map((record) => record.id);
          this.records.set(merged);
          this.mode.set(normalizeOpenMode(stored.projectOpenMode));
          this.restoreStatus = 'done';
          if (dropped.length) this.dropLocalAdditions(dropped);
          if (this.persistPending) this.persistRequests.next();
        },
        error: () => {
          if (generation === this.generation) this.restoreStatus = 'idle';
        },
      });
  }

  /** True when the project is already open, or fewer than the cap are open. */
  canOpenHere(projectId: string): boolean {
    return !!this.recordOf(projectId) || this.records().length < MAX_OPEN_PROJECTS;
  }

  /** Adds the project (or refreshes its name) and shows it inside the cabinet. */
  open(project: OpenableProject): void {
    if (!this.available || !isProjectId(project.id)) return;
    if (this.restoreStatus === 'idle') this.restore();
    const existing = this.recordOf(project.id);
    if (existing) {
      this.updateRecord(project.id, {
        name: project.name || existing.name,
        kind: project.projectType ? openProjectKindOf(project.projectType) : existing.kind,
      });
    } else if (!this.addRecord(project.id, project.name || PLACEHOLDER_PROJECT_NAME, openProjectKindOf(project.projectType))) {
      return;
    }
    void this.router.navigate(['/user/projects/open', project.id]);
  }

  /** Shows a project; called when its route is entered. */
  activate(id: string): void {
    if (!this.available || !isProjectId(id)) return;
    if (this.restoreStatus === 'idle') this.restore();
    if (!this.recordOf(id) && !this.addRecord(id, PLACEHOLDER_PROJECT_NAME, 'editor')) {
      void this.router.navigateByUrl('/user/projects');
      return;
    }
    const previous = this.activeIdSignal();
    if (previous && previous !== id) this.sendToBackground(previous);
    this.activeIdSignal.set(id);
    this.updateRecord(id, { lastActiveAt: new Date().toISOString() });
    this.bringToFront(id);
    this.enforcePool();
  }

  /** Hides the visible project and keeps its editor paused; called when its route is left. */
  deactivate(): void {
    const previous = this.activeIdSignal();
    if (!previous) return;
    this.activeIdSignal.set(null);
    this.sendToBackground(previous);
    this.enforcePool();
  }

  /** Closes a project, asking first when its editor has unsaved changes. */
  close(id: string): void {
    const view = this.views().find((entry) => entry.id === id);
    if (!view) return;
    if (!view.dirty) {
      this.closeNow(id, true);
      return;
    }
    this.confirmDialog
      .open({
        title: 'Close project',
        subject: view.name,
        question: 'Close this project?',
        message: 'It has unsaved CAD changes. Closing it discards them.',
        confirmLabel: 'Close project',
        tone: 'danger',
      })
      .subscribe((confirmed) => {
        if (confirmed) this.closeNow(id, true);
      });
  }

  retry(id: string): void {
    if (this.stateOf(id) === 'failed') this.mount(id);
  }

  openInNewTab(id: string): void {
    this.win.open(editorProjectUrl(environment.EDITOR_ADDRESS, id), '_blank');
  }

  /** Connects the bridge to an iframe the host created for a mounted project. */
  attachFrame(key: number, frame: EditorFrame): void {
    const origin = this.editorOrigin;
    const mounted = this.mountedFrames().find((entry) => entry.key === key);
    if (!origin || !mounted) return;
    this.endpoints.get(mounted.id)?.stop();
    const endpoint = new BridgeEndpoint<EditorToHostMessage, HostToEditorMessage>({
      listenOn: this.win,
      peer: () => frame.contentWindow,
      peerOrigin: origin,
      ownSource: 'infinitform-cabinet',
      parse: parseEditorToHost,
    });
    endpoint.start((message) => this.onEditorMessage(mounted.id, message));
    this.endpoints.set(mounted.id, endpoint);
  }

  frameUrl(id: string): string {
    return editorProjectUrl(environment.EDITOR_ADDRESS, id, { embedded: true });
  }

  setOpenMode(mode: ProjectOpenMode, persist: boolean): void {
    this.mode.set(mode);
    if (!persist) return;
    this.settings.update({ projectOpenMode: mode }).subscribe({
      error: () => this.toast.error("Couldn't save your choice. It applies until you sign out."),
    });
  }

  /** Writes a pending list change now. */
  flushNow(): void {
    if (!this.persistPending || this.restoreStatus !== 'done') return;
    this.persistPending = false;
    const generation = this.generation;
    this.settings.update({ openProjects: this.records().map((record) => ({ ...record })) }).subscribe({
      error: () => {
        if (generation === this.generation) this.persistPending = true;
      },
    });
  }

  /** Signs every editor out, removes every iframe and forgets the list until the next restore. */
  teardown(): void {
    this.generation += 1;
    for (const id of this.readyIds) this.endpoints.get(id)?.send({ type: 'logout', payload: null });
    for (const frame of [...this.mountedFrames()]) this.unmount(frame.id);
    for (const entry of this.runtime().values()) {
      if (entry.snapshotUrl) this.win.URL.revokeObjectURL(entry.snapshotUrl);
    }
    this.runtime.set(new Map());
    this.records.set([]);
    this.activeIdSignal.set(null);
    this.mode.set('ask');
    this.restoreStatus = 'idle';
    this.persistPending = false;
  }

  /**
   * Hands a new grant to every ready editor. Only a change from a grant to
   * none is a sign-out; no grant before the first one leaves everything as is.
   */
  private onGrant(grant: EmbeddedAuthPayload | null): void {
    const previous = this.lastGrant;
    this.lastGrant = grant;
    if (grant) {
      if (previous && previous.profile.email !== grant.profile.email) {
        // Another tab signed out and in as someone else; the shared refresh
        // cookie handed this tab the new user's token. Start clean instead
        // of broadcasting it to the old user's editors or saving over their list.
        const wasShowingProject = this.activeIdSignal() !== null;
        this.teardown();
        this.lastRenewedAt = null;
        this.restore();
        if (wasShowingProject) void this.router.navigateByUrl('/user/projects');
        return;
      }
      if (previous && previous !== grant) this.lastRenewedAt = Date.now();
      this.broadcast({ type: 'auth', payload: grant });
    } else if (previous) {
      this.lastRenewedAt = null;
      this.teardown();
    }
  }

  private onEditorMessage(id: string, message: EditorToHostMessage): void {
    switch (message.type) {
      case 'ready':
        this.onReady(id);
        break;
      case 'auth-request':
        this.onAuthRequest(id);
        break;
      case 'meta':
        this.onMeta(id, message.payload.name, message.payload.kind);
        break;
      case 'title':
        if (this.recordOf(id)) this.patchRuntime(id, { title: message.payload.text });
        break;
      case 'dirty':
        this.onDirty(id, message.payload.value);
        break;
      case 'loading':
        if (this.recordOf(id)) this.patchRuntime(id, { editorLoading: message.payload.value });
        break;
      case 'snapshot':
        this.onSnapshot(id, message.payload.blob);
        break;
      case 'hibernate-ack':
        if (this.stateOf(id) === 'hibernating' || !this.recordOf(id)) this.finishHibernate(id);
        break;
      case 'navigate':
        if (id === this.activeIdSignal()) void this.router.navigateByUrl(message.payload.cabinetPath);
        break;
      case 'close':
        this.close(id);
        break;
      case 'not-found':
      case 'forbidden':
        this.dropUnavailable(id);
        break;
    }
  }

  private onReady(id: string): void {
    if (!this.mountedFrames().some((frame) => frame.id === id)) return;
    // A closed or unloading editor that reloaded has nothing left to save: remove its frame.
    if (!this.recordOf(id)) {
      this.unmount(id);
      return;
    }
    if (this.stateOf(id) === 'hibernating') {
      this.finishHibernate(id);
      return;
    }
    this.clearTimer(id);
    this.readyIds.add(id);
    const grant = this.auth.embeddedAuth();
    if (grant) this.send(id, { type: 'auth', payload: grant });
    this.send(id, { type: 'theme', payload: { mode: this.theme.currentTheme() } });
    if (id === this.activeIdSignal()) {
      this.patchRuntime(id, { state: 'live' });
    } else {
      this.patchRuntime(id, { state: 'warm' });
      this.send(id, { type: 'pause', payload: null });
      this.enforcePool();
    }
  }

  private onAuthRequest(id: string): void {
    const grant = this.auth.embeddedAuth();
    if (!grant || this.renewing) return;
    if (this.lastRenewedAt !== null && Date.now() - this.lastRenewedAt < AUTH_REQUEST_RENEW_COOLDOWN_MS) {
      this.send(id, { type: 'auth', payload: grant });
      return;
    }
    // The renewed grant reaches every ready editor through the embeddedAuth effect;
    // a failed renewal signs the cabinet out, which tears every editor down.
    this.renewing = true;
    this.auth.refreshToken().subscribe({
      next: () => {
        this.lastRenewedAt = Date.now();
      },
      error: () => {
        this.renewing = false;
      },
      complete: () => {
        this.renewing = false;
      },
    });
  }

  private onMeta(id: string, name: string, kind: OpenProjectKind): void {
    const record = this.recordOf(id);
    if (record && (record.name !== name || record.kind !== kind)) this.updateRecord(id, { name, kind });
  }

  private onDirty(id: string, dirty: boolean): void {
    if (!this.recordOf(id)) return;
    this.patchRuntime(id, { dirty });
    if (!dirty) this.enforcePool();
  }

  private onSnapshot(id: string, blob: Blob): void {
    if (!this.recordOf(id)) return;
    const previous = this.runtime().get(id)?.snapshotUrl;
    if (previous) this.win.URL.revokeObjectURL(previous);
    this.patchRuntime(id, { snapshotUrl: this.win.URL.createObjectURL(blob) });
  }

  private dropUnavailable(id: string): void {
    const record = this.recordOf(id);
    if (!record) return;
    this.closeNow(id, false);
    this.toast.error(`"${record.name}" is no longer available to you, so it was removed from your open projects.`);
  }

  /**
   * Cleans up projects `mergeRestored` dropped because they were added
   * locally while the restore was in flight and there was no room left for
   * them once the saved list arrived. `records` was already updated to
   * leave them out; this unmounts any frame they had and revokes any
   * snapshot, the same cleanup `forget` does, without touching `records`
   * again or scheduling another save (the merged list is already what gets
   * persisted next). The active one, if it was among them, goes back to the
   * project list exactly like closing it would.
   */
  private dropLocalAdditions(ids: readonly string[]): void {
    for (const id of ids) {
      this.unmount(id);
      const snapshotUrl = this.runtime().get(id)?.snapshotUrl;
      if (snapshotUrl) this.win.URL.revokeObjectURL(snapshotUrl);
      this.runtime.update((map) => {
        const next = new Map(map);
        next.delete(id);
        return next;
      });
    }
    this.toast.error(`You already have ${MAX_OPEN_PROJECTS} projects open. Close one to open another here.`);
    const activeId = this.activeIdSignal();
    if (activeId !== null && ids.includes(activeId)) {
      this.activeIdSignal.set(null);
      void this.router.navigateByUrl('/user/projects');
    }
  }

  private addRecord(id: string, name: string, kind: OpenProjectKind): boolean {
    if (this.records().length >= MAX_OPEN_PROJECTS) {
      this.toast.error(`You already have ${MAX_OPEN_PROJECTS} projects open. Close one to open another here.`);
      return false;
    }
    const now = new Date().toISOString();
    const record: OpenProjectRecord = { id, name, kind, openedAt: now, lastActiveAt: now };
    this.records.update((list) => [...list, record]);
    this.schedulePersist();
    return true;
  }

  private closeNow(id: string, graceful: boolean): void {
    const wasActive = this.activeIdSignal() === id;
    if (graceful && this.readyIds.has(id)) {
      // The editor saves what is pending before its frame goes away.
      this.send(id, { type: 'hibernate', payload: null });
      this.startTimer(id, HIBERNATE_ACK_TIMEOUT_MS, () => this.unmount(id));
    } else {
      this.unmount(id);
    }
    this.forget(id);
    if (wasActive) {
      this.activeIdSignal.set(null);
      void this.router.navigateByUrl('/user/projects');
    }
  }

  private forget(id: string): void {
    const snapshotUrl = this.runtime().get(id)?.snapshotUrl;
    if (snapshotUrl) this.win.URL.revokeObjectURL(snapshotUrl);
    this.runtime.update((map) => {
      const next = new Map(map);
      next.delete(id);
      return next;
    });
    this.records.update((list) => list.filter((record) => record.id !== id));
    this.schedulePersist();
  }

  private bringToFront(id: string): void {
    switch (this.stateOf(id)) {
      case 'warm':
        this.patchRuntime(id, { state: 'live' });
        this.send(id, { type: 'resume', payload: null });
        break;
      case 'cold':
      case 'hibernating':
      case 'hibernated':
      case 'failed':
        this.mount(id);
        break;
      case 'live':
      case 'booting':
        break;
    }
  }

  private sendToBackground(id: string): void {
    if (this.stateOf(id) !== 'live') return;
    this.patchRuntime(id, { state: 'warm' });
    this.send(id, { type: 'snapshot-request', payload: null });
    this.send(id, { type: 'pause', payload: null });
  }

  private enforcePool(): void {
    const runtime = this.runtime();
    const lastActive = new Map(this.records().map((record) => [record.id, record.lastActiveAt]));
    const members: PoolMember[] = this.mountedFrames().flatMap((frame) => {
      const entry = runtime.get(frame.id);
      const lastActiveAt = lastActive.get(frame.id);
      return entry && lastActiveAt ? [{ id: frame.id, state: entry.state, dirty: entry.dirty, lastActiveAt }] : [];
    });
    for (const id of pickHibernations(members, this.activeIdSignal(), this.warmLimit)) this.hibernate(id);
  }

  private hibernate(id: string): void {
    const state = this.stateOf(id);
    if (state === 'booting') {
      this.unmount(id);
      this.patchRuntime(id, { state: this.runtime().get(id)?.snapshotUrl ? 'hibernated' : 'cold' });
      return;
    }
    if (state !== 'warm' && state !== 'live') return;
    this.patchRuntime(id, { state: 'hibernating' });
    this.send(id, { type: 'hibernate', payload: null });
    this.startTimer(id, HIBERNATE_ACK_TIMEOUT_MS, () => this.finishHibernate(id));
  }

  private finishHibernate(id: string): void {
    this.unmount(id);
    if (this.recordOf(id)) this.patchRuntime(id, { state: 'hibernated' });
  }

  private mount(id: string): void {
    this.unmount(id);
    this.mountCounter += 1;
    const key = this.mountCounter;
    this.mountedFrames.update((frames) => [...frames, { id, key }]);
    this.patchRuntime(id, { state: 'booting', editorLoading: false });
    this.startTimer(id, READY_TIMEOUT_MS, () => {
      this.unmount(id);
      if (this.recordOf(id)) this.patchRuntime(id, { state: 'failed' });
    });
  }

  private unmount(id: string): void {
    this.clearTimer(id);
    this.endpoints.get(id)?.stop();
    this.endpoints.delete(id);
    this.readyIds.delete(id);
    if (this.mountedFrames().some((frame) => frame.id === id)) {
      this.mountedFrames.update((frames) => frames.filter((frame) => frame.id !== id));
    }
  }

  private send(id: string, message: HostToEditorMessage): void {
    if (this.readyIds.has(id)) this.endpoints.get(id)?.send(message);
  }

  private broadcast(message: HostToEditorMessage): void {
    for (const id of this.readyIds) this.endpoints.get(id)?.send(message);
  }

  private schedulePersist(): void {
    this.persistPending = true;
    if (this.restoreStatus === 'done') this.persistRequests.next();
  }

  private readonly onPageHide = (): void => {
    if (!this.persistPending || this.restoreStatus !== 'done') return;
    const token = this.auth.getAccessToken();
    if (!token) return;
    this.persistPending = false;
    this.win
      .fetch(`${environment.API_ADDRESS}/user/settings`, {
        method: 'POST',
        keepalive: true,
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ openProjects: this.records() }),
      })
      .catch(() => undefined);
  };

  private recordOf(id: string): OpenProjectRecord | undefined {
    return this.records().find((record) => record.id === id);
  }

  private updateRecord(id: string, patch: Partial<OpenProjectRecord>): void {
    this.records.update((list) => list.map((record) => (record.id === id ? { ...record, ...patch } : record)));
    this.schedulePersist();
  }

  private stateOf(id: string): OpenProjectState {
    return this.runtime().get(id)?.state ?? 'cold';
  }

  private patchRuntime(id: string, patch: Partial<EntryRuntime>): void {
    this.runtime.update((map) => {
      const next = new Map(map);
      next.set(id, { ...(map.get(id) ?? IDLE), ...patch });
      return next;
    });
  }

  private startTimer(id: string, ms: number, run: () => void): void {
    this.clearTimer(id);
    this.timers.set(
      id,
      setTimeout(() => {
        this.timers.delete(id);
        run();
      }, ms)
    );
  }

  private clearTimer(id: string): void {
    const timer = this.timers.get(id);
    if (timer !== undefined) clearTimeout(timer);
    this.timers.delete(id);
  }
}
